import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import Button from "@material-ui/core/Button";

import { getGaugeTypeText, isEllipsisActive } from "utils/utils";
import { BarchartWidgetFormStrings, FORMATTED_WIDGET_TYPE, ReportTitleStrings } from "constants/constantStrings";
import { NORMALIZED_FILTERS, NormalizedFiltersStrings } from "constants/report.constant";
import { ReportWidgetConfigs } from "constants/widgets.constant";
import { WIDGET_TYPES } from "constants/widgets.type.const";
import { getReportWidgetsMenuItems } from "constants/widgetsMenu.config";

import DupontCheckbox from "components/DupontCheckbox";
import { DupontTooltip } from "components/DupontTooltip";
import PopoverMenu from "components/PopoverMenu";
import PopoverText from "components/PopoverText";

import MenuIcon from "assets/SvgIcon/cardviewMenu/menu_Default";

import { PREDICTION_TIMES } from "../PredictionWidgetForm/PredictionForm.const";

const ReportTitle = ({ report, baselineList, tags, parentStyle, onWidgetAction, onWidgetEnable }) => {
  const attributes = report?.Attributes;
  const systemTag = attributes?.SystemTag;
  report.systemTagName = systemTag?.map(tagId => tags[tagId]?.TagName);
  const [menuTargetEl, setMenuTargetEl] = React.useState(null);
  const menuHandler = event => setMenuTargetEl(event.currentTarget);
  const [titleEllipse, setTitleEllipse] = useState(false);
  const titleRef = useRef(null);
  const tagString = systemTag ? systemTag.join(",\r\n ") : "";
  const [baselineValuesMessage, setBaselineValuesMessage] = useState("");
  report.systemTagName = report.systemTagName ? report.systemTagName.join(",\r\n ") : "";
  
  const getBaselineName = baselineId => baselineList?.find(({ Id }) => `${Id}` === `${baselineId}`)?.Name;
  useEffect(() => {
    if (report.Type === WIDGET_TYPES.NORMALIZATION && baselineList) {
      report.normalizeBaseline = getBaselineName(attributes?.Baseline);
    }
  }, [baselineList]);

  if (report.Type === WIDGET_TYPES.PREDICTION) {
    report.predictionTime = PREDICTION_TIMES[attributes.Duration];
    report.predictionNote = ReportTitleStrings.predctionNote;
  }

  const getFormattedThresholds = (low, high) => (low && high && ` ${ReportTitleStrings.low} ${low}, ${ReportTitleStrings.high} ${high}`) || "";

  if (report.Type === WIDGET_TYPES.NORMALIZATION && attributes) {
    const { RecoveryL, RecoveryH, FeedFlowL, FeedFlowH, RejectConductivityL, RejectConductivityH } = attributes;
    report.normalizeRecovery = getFormattedThresholds(RecoveryL, RecoveryH);
    report.normalizeFeedFlow = getFormattedThresholds(FeedFlowL, FeedFlowH);
    report.normalizeRejectConductivity = getFormattedThresholds(RejectConductivityL, RejectConductivityH);
    report.normalizeBaseline = getBaselineName(attributes?.Baseline);
  }

  const getNormazliedDesc = () => (
    <>
      {getTitleTemplate(NormalizedFiltersStrings[NORMALIZED_FILTERS.production], report.normalizeRecovery, "zeroMargin")}
      {getTitleTemplate(NormalizedFiltersStrings[NORMALIZED_FILTERS.feedFlow], report.normalizeFeedFlow, "zeroMargin")}
      {getTitleTemplate(NormalizedFiltersStrings[NORMALIZED_FILTERS.conductivity], report.normalizeRejectConductivity, "zeroMargin")}
      {getTitleTemplate(ReportTitleStrings.baseline, report.normalizeBaseline, "zeroMargin")}
      {baselineValuesMessage && getTitleTemplate(ReportTitleStrings.Note, baselineValuesMessage, "zeroMargin")}
    </>
  );

  const onMenuClose = data => {
    setMenuTargetEl(null);
    if (data) {
      onWidgetAction(data.id, report.Id);
    }
  };

  const isToShowLevel = () => {
    const { Type } = report;
    return Type !== WIDGET_TYPES.GAUGE;
  };

  const getWidgetType = () => {
    const { Type } = report;
    return (
      <div className="smalldescriptionText icon__border d-flex marginTop5 marginBtm5 fitContent margin-left-20">
        <img src={ReportWidgetConfigs[Type]?.icon} alt={Type} className="whiteBackground" height="32px" width="32px" />
        <div className="flex-center-center margin-right-10 iconText" style={{ color: ReportWidgetConfigs[Type]?.color }}>
          {FORMATTED_WIDGET_TYPE[Type]}
        </div>
      </div>
    );
  };

  const getReportHeader = () => (report.Header ? report.Header : report.Tags && report.Tags.length > 0 ? report.Tags[0].TagName : "");

  useLayoutEffect(() => {
    setTitleEllipse(isEllipsisActive(titleRef));
  }, [titleRef?.current?.scrollWidth]);

  const getLevelTypeLabel = level => (level && BarchartWidgetFormStrings[level]) || level;

  const getTitleTemplate = (key, value, style = "") => (
    <div className={`reportTags ${style}`}>
      <span className="report-title__key">{key}</span>
      <span>{value}</span>
    </div>
  );
  useEffect(() => {
    if (systemTag?.length > 0) {
      const hasAnalyticsNormalizedTag = systemTag.some(
        item =>
          item=== "normalized_permeate_flow" || item === "normalized_differential_pressure" || item=== "normalized_salt_passage"
      );
      if (hasAnalyticsNormalizedTag) setBaselineValuesMessage(ReportTitleStrings.baselineValuesMessage);
      else setBaselineValuesMessage("");
    }
  }, [systemTag]);

  return (
    <div className={parentStyle}>
      <div className="flex-between-center">
        <DupontCheckbox checked={report.Enabled} onChange={event => onWidgetEnable(event, report)} />
        <DupontTooltip title={getReportHeader()} placement="bottom-start" type={titleEllipse ? "primary" : "none"}>
          <div className="reportTitle " ref={titleRef}>
            {getReportHeader()}
          </div>
        </DupontTooltip>
        <div className="margin-left-auto ">
          <Button onClick={menuHandler}>
            <MenuIcon />
          </Button>
          <PopoverMenu menuItems={getReportWidgetsMenuItems(report.Type)} parentElem={menuTargetEl} onMenuClose={onMenuClose} />
        </div>
      </div>
      {getWidgetType()}
      {tags && tagString !== "" && (
        <>
          {getTitleTemplate(ReportTitleStrings.tags, tagString)}
          {getTitleTemplate(ReportTitleStrings.tagName, report.systemTagName)}
        </>
      )}
      {attributes.Function && isToShowLevel() && getTitleTemplate(ReportTitleStrings.levelType, getLevelTypeLabel(attributes?.Function))}
      {report.SubType &&
        (report.Type === WIDGET_TYPES.GAUGE
          ? getTitleTemplate(ReportTitleStrings.gaugeType, getGaugeTypeText(report.SubType))
          : getTitleTemplate(ReportTitleStrings.modelType, report.SubType))}
      {report.predictionTime && getTitleTemplate(ReportTitleStrings.predictionTime, report.predictionTime)}
      {report.predictionNote && (
        <div className="predictionNote">
          <span>{report.predictionNote}</span>
        </div>
      )}

      {report.Type === "Normalization" && (
        <div className="normalized-thresold--popover">
          <PopoverText
            descriptor={<u>{ReportTitleStrings.thresholdBaseline}</u>}
            descriptionText={getNormazliedDesc()}
            iconPosition={75}
            customClass="normalized-thresold--popover_text"           
          />          
        </div>
      )}

      {attributes?.LL && getTitleTemplate(ReportTitleStrings.ll, attributes.LL)}
      {attributes?.L && getTitleTemplate(ReportTitleStrings.l, attributes.L)}
      {attributes?.HH && getTitleTemplate(ReportTitleStrings.hh, attributes.HH)}
      {attributes?.H && getTitleTemplate(ReportTitleStrings.h, attributes.H)}
    </div>
  );
};

export default ReportTitle;
